import { api, type ApiError } from "./client";
import { useResource } from "./useResource";

// GET /api/v1/me/sessions
export type ApiSession = {
  readonly created_at: string;
  readonly current: boolean;
  readonly expires_at: string;
  readonly last_seen_at: string | null;
  readonly public_id: string;
  readonly remote_ip: string | null;
  readonly user_agent: string | null;
};

// Where a native client runs. The closed vocabulary the server enforces with a
// check constraint and its own validator (auth-and-sessions.md §App sessions),
// mirrored as a union for the same reason every other server vocabulary here is
// — a total `Record` keyed by it cannot be missing a case.
export type AppPlatform =
  "android" | "ios" | "ipados" | "linux" | "macos" | "windows";

// GET /api/v1/me/app-sessions — the native apps signed in to this account
// (auth-and-sessions.md §App sessions). A separate resource from `/me/sessions`
// because it is a separate credential with its own revoke endpoint; Settings
// shows the two as one list of devices, which is a presentation choice.
export type ApiAppSession = {
  readonly client_name: string;
  readonly client_platform: AppPlatform;
  readonly created_at: string;
  readonly current: boolean;
  readonly expires_at: string;
  readonly last_seen_at: string | null;
  readonly public_id: string;
  readonly refreshed_at: string | null;
  readonly remote_ip: string | null;
  readonly user_agent: string | null;
};

// GET /api/v1/me/api-tokens
export type ApiToken = {
  readonly created_at: string;
  readonly expires_at: string | null;
  readonly last_used_at: string | null;
  readonly name: string;
  readonly public_id: string;
  readonly scopes: Record<string, string>;
  readonly token_prefix: string;
  readonly workspace_id: number | null;
};

// POST /api/v1/me/api-tokens — the plaintext token is returned exactly once.
export type CreatedApiToken = {
  readonly public_id: string;
  readonly token: string;
  readonly token_prefix: string;
};

export type UseSessions = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly sessions: ReadonlyArray<ApiSession>;
};

export const useSessions = (): UseSessions => {
  const { data, error, loading, refetch } =
    useResource<ReadonlyArray<ApiSession>>("/me/sessions");
  return { error, loading, refetch, sessions: data ?? [] };
};

export const revokeSession = (id: string): Promise<unknown> =>
  api.del(`/me/sessions/${id}`);

export type UseAppSessions = {
  readonly appSessions: ReadonlyArray<ApiAppSession>;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
};

export const useAppSessions = (): UseAppSessions => {
  const { data, error, loading, refetch } =
    useResource<ReadonlyArray<ApiAppSession>>("/me/app-sessions");
  return { appSessions: data ?? [], error, loading, refetch };
};

export const revokeAppSession = (id: string): Promise<unknown> =>
  api.del(`/me/app-sessions/${id}`);

export type UseApiTokens = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly tokens: ReadonlyArray<ApiToken>;
};

export const useApiTokens = (): UseApiTokens => {
  const { data, error, loading, refetch } =
    useResource<ReadonlyArray<ApiToken>>("/me/api-tokens");
  return { error, loading, refetch, tokens: data ?? [] };
};

export const createApiToken = (name: string): Promise<CreatedApiToken> =>
  api.post<CreatedApiToken>("/me/api-tokens", { name });

export const revokeApiToken = (id: string): Promise<unknown> =>
  api.del(`/me/api-tokens/${id}`);

// PUT /api/v1/me/email — start an email-change intent (auth-and-sessions.md
// §Email change). The server emails a confirmation link to the NEW address;
// the change takes effect only once that link is clicked. `confirmation_token`
// is surfaced for dev environments without a real SMTP sender.
export type EmailChangeStarted = {
  readonly confirmation_token: string;
  readonly intent_public_id: string;
};

export const changeEmail = (newEmail: string): Promise<EmailChangeStarted> =>
  api.put<EmailChangeStarted>("/me/email", { new_email: newEmail });

// POST /api/v1/me/email/confirm — finish the change from the page the emailed
// link lands on (pages/confirm-email). Unauthenticated: the token is the whole
// credential, so this works from a browser that was never signed in. Returns
// the address the account now uses.
export type EmailChangeConfirmed = {
  readonly email: string;
};

export const confirmEmailChange = (
  token: string,
): Promise<EmailChangeConfirmed> =>
  api.post<EmailChangeConfirmed>("/me/email/confirm", { token });

// GET /api/v1/me/agent-credential — the Claude credential handed to the
// sandboxes of tasks you create (sandboxes.md §Agent credentials). The stored
// value is never returned; `hint` is its last four characters.
export type AgentCredentialKind = "api_key" | "oauth";

export type AgentCredentialStatus = {
  readonly configured: boolean;
  readonly hint: string | null;
  readonly kind: AgentCredentialKind | null;
  readonly updated_at: string | null;
};

export type UseAgentCredential = {
  readonly credential: AgentCredentialStatus | undefined;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
};

export const useAgentCredential = (): UseAgentCredential => {
  const { data, error, loading, refetch } = useResource<AgentCredentialStatus>(
    "/me/agent-credential",
  );
  return { credential: data ?? undefined, error, loading, refetch };
};

export const setAgentCredential = (
  kind: AgentCredentialKind,
  value: string,
): Promise<unknown> => api.put("/me/agent-credential", { kind, value });

export const clearAgentCredential = (): Promise<unknown> =>
  api.del("/me/agent-credential");
